import { useCallback, useState } from "react";
import { Input, Modal, Typography, message } from "antd";
import { submitFeedback } from "../../utils/feedbackApi";
import { useTranslation } from "../../i18n";
import "./feedback.css";

const { Text } = Typography;

function FeedbackPrompt({ sourceType, linkId, albumPath, albumName, className = "" }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [content, setContent] = useState("");
    const [contact, setContact] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const resetForm = useCallback(() => {
        setContent("");
        setContact("");
    }, []);

    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        if (submitting) {
            return;
        }
        setOpen(false);
        resetForm();
    }, [resetForm, submitting]);

    const handleSubmit = useCallback(async () => {
        const trimmed = content.trim();
        if (!trimmed) {
            message.warning(t("feedback.modal.contentRequired"));
            return;
        }
        setSubmitting(true);
        try {
            await submitFeedback({
                sourceType,
                linkId,
                albumPath,
                albumName,
                content: trimmed,
                contact: contact.trim() || undefined,
            });
            message.success(t("feedback.modal.success"));
            setOpen(false);
            resetForm();
        } catch (error) {
            message.error(error?.message || t("feedback.modal.error"));
        } finally {
            setSubmitting(false);
        }
    }, [albumName, albumPath, contact, content, linkId, resetForm, sourceType, t]);

    if (!linkId || !albumPath || !albumName) {
        return null;
    }

    return (
        <>
            <p className={`feedback-prompt${className ? ` ${className}` : ""}`}>
                {t("feedback.prompt")}{" "}
                <button type="button" className="feedback-prompt__link" onClick={handleOpen}>
                    {t("feedback.link")}
                </button>
            </p>
            <Modal
                open={open}
                title={t("feedback.modal.title")}
                onCancel={handleClose}
                onOk={handleSubmit}
                okText={t("feedback.modal.submit")}
                cancelText={t("feedback.modal.cancel")}
                confirmLoading={submitting}
                destroyOnClose
            >
                <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                    {albumName}
                </Text>
                <Input.TextArea
                    value={content}
                    onChange={(event) => setContent(event.target.value)}
                    placeholder={t("feedback.modal.contentPlaceholder")}
                    autoSize={{ minRows: 4, maxRows: 10 }}
                    maxLength={2000}
                    autoFocus
                />
                <Input
                    value={contact}
                    onChange={(event) => setContact(event.target.value)}
                    placeholder={t("feedback.modal.contactPlaceholder")}
                    maxLength={200}
                    style={{ marginTop: 12 }}
                />
            </Modal>
        </>
    );
}

export default FeedbackPrompt;
